import {
	type SDK_Filter,
	clientConfig,
	demoFilters,
	filterTemplate,
	getSdk,
	strings,
} from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { atom, selector } from "recoil";
import { fireUpdateFilterNamesEvent } from "../lib/editor/events";
import filterKeywordParser from "../lib/filterKeywordParser";
import { sendFilterNamesToViewer } from "../lib/messageBus";
import { HostId, ParsedStackAtom, ViewerPageIndex } from "./atoms";
import { Authorization } from "./authAtoms";
import { CodeMirrorCodeState } from "./codeAtom";
import { StackCfKeyAtom } from "./stackAtoms";
import { DemoMode } from "./uiAtoms";

export const FiltersAreLoaded = atom<number>({
	key: "FiltersAreLoaded",
	default: 0,
});

export const PreviewerFontCSS = atom<string>({
	key: "PreviewerFontCSS",
	default: "",
});

export const FilterNamesSelector = selector<string[]>({
	key: "FilterNamesSelector",
	get: async ({ get }) => {
		const client = new GraphQLClient(clientConfig.gqlGetFilter());
		const sdk = getSdk(client);
		const user = getAuth().currentUser;
		const hostId = get(HostId);
		const inDemoMode = get(DemoMode);
		if (inDemoMode) {
			sendFilterNamesToViewer(hostId, demoFilters);
			return demoFilters;
		}
		// require auth to rerun selector
		const auth = get(Authorization);
		if (!user || !auth.isLoggedIn) return [];
		const jwt = await getIdToken(user);
		return sdk
			.GetFilterNames({ jwt })
			.then((res) => {
				const names = res.getFilterNames.map(
					(filter) => `${filter.ownerName}::${filter.filterName}`,
				);
				names.push(strings.utility.defaultFilter);
				fireUpdateFilterNamesEvent(names);
				sendFilterNamesToViewer(hostId, names);

				return names;
			})
			.catch((e) => {
				console.error("filterNamesEffect: ", e);
				return [];
			});
	},
});

export const FilterMapSelector = selector<string[]>({
	key: "FilterMapsSelector",
	get: ({ get }) => {
		const stackCfKey = get(StackCfKeyAtom);
		const code = get(CodeMirrorCodeState(stackCfKey));
		const parsed = filterKeywordParser(code);
		return parsed;
	},
});

export const ActiveFilterNameSelector = selector<string>({
	key: "ActiveFilterNameSelector",
	get: ({ get }) => {
		const pageIndex = get(ViewerPageIndex);
		const parsedStack = get(ParsedStackAtom);

		let activeFilterName: string = strings.defaultFilter;

		if (parsedStack) {
			// Iterate up to the current page (1-based index converted to 0-based loop limit)
			for (let i = 0; i < pageIndex && i < parsedStack.length; i++) {
				const page = parsedStack[i];
				// Find the last filter definition in the page elements
				const filterLine = page.find((line) => line.type === "filter:");
				if (filterLine) {
					activeFilterName = filterLine.content.trim();
				}
			}
		}
		return activeFilterName;
	},
});

export const ActiveFilterSelector = selector<SDK_Filter | undefined>({
	key: "ActiveFilterSelector",
	get: async ({ get }) => {
		const activeFilterName = get(ActiveFilterNameSelector);
		const filterNames = get(FilterNamesSelector);

		if (activeFilterName === strings.defaultFilter) {
			// @ts-ignore
			return filterTemplate;
		}

		if (!filterNames.includes(activeFilterName)) {
			return undefined;
		}

		// 2. Fetch Filter
		const parts = activeFilterName.split("::");
		if (parts.length < 2) return undefined;

		const ownerName = parts[0];
		const filterName = parts[1];

		const client = new GraphQLClient(clientConfig.gqlGetFilter());
		const sdk = getSdk(client);

		try {
			const res = await sdk.GetFilter({ ownerName, filterName });
			if (res.getFilter) {
				return res.getFilter;
			}
		} catch (e) {
			console.error("Failed to fetch active filter", e);
		}

		return undefined;
	},
});
